/**
 * Конверт ответа native-контракта 1С v1 (`business-openapi-v1.json`):
 * `{ resourceId, configurationVersion, correlation, data }` и заголовки
 * свежести `X-Resource-Freshness | Observed-At | Stale-Reason` (B4a).
 *
 * Свежесть не выдумывается: пока transport не отдаёт заголовки (запрос
 * интегратору в evidence UB1b), она `unknown` — и экран так и пишет, а не
 * считает данные актуальными.
 */
import { asObject, asString, field, fail } from './decode'

export type FreshnessKind = 'fresh' | 'stale' | 'unknown'

export interface Freshness {
  kind: FreshnessKind
  /** Когда 1С читали в последний раз (ISO), если сервер сообщил. */
  observedAt: string | null
  /** Причина устаревания дословно (причина отказа 1С), если сервер сообщил. */
  reason: string | null
}

export const UNKNOWN_FRESHNESS: Freshness = { kind: 'unknown', observedAt: null, reason: null }

/** Ответ native-чтения: данные + то, что экрану надо знать о них. */
export interface NativeRead<T> {
  data: T
  /** Ревизия настроек базы: уходит в `expectedConfigurationVersion` команд. */
  configurationVersion: string
  freshness: Freshness
  correlationId: string
}

/** Данные конверта; ресурс из конверта сверяется с запрошенным. */
export function decodeEnvelope<T>(
  raw: unknown,
  resourceId: string,
  readData: (data: unknown, path: string) => T,
): { data: T; configurationVersion: string } {
  const envelope = asObject(raw, 'ответ')
  const rid = field(envelope, 'resourceId', 'ответ')
  const echoed = typeof rid === 'string' ? rid : asString(asObject(rid, 'ответ.resourceId').value, 'ответ.resourceId.value')
  // Чужая база в ответе на запрос этой — отказ, а не молчаливое смешение (D-064).
  if (echoed.toLowerCase() !== resourceId.toLowerCase()) fail('ответ относится к другому ресурсу')
  const configurationVersion = asString(field(envelope, 'configurationVersion', 'ответ'), 'ответ.configurationVersion')
  return { data: readData(field(envelope, 'data', 'ответ'), 'data'), configurationVersion }
}

function headerOf(headers: unknown, name: string): string | null {
  if (typeof headers !== 'object' || headers === null) return null
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === name && typeof value === 'string') return value
  }
  return null
}

function decodeReason(value: string | null): string | null {
  if (value === null) return null
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * Свежесть из меты ответа. Мета может не нести заголовков (transport без
 * расширения) — тогда `unknown`, а не `fresh`.
 */
export function freshnessOf(meta: unknown): Freshness {
  const headers = typeof meta === 'object' && meta !== null ? (meta as { headers?: unknown }).headers : undefined
  const kind = headerOf(headers, 'x-resource-freshness')
  if (kind !== 'fresh' && kind !== 'stale') return UNKNOWN_FRESHNESS
  return {
    kind,
    observedAt: headerOf(headers, 'x-resource-observed-at'),
    reason: decodeReason(headerOf(headers, 'x-resource-stale-reason')),
  }
}

export function correlationOf(meta: unknown): string {
  const id = typeof meta === 'object' && meta !== null ? (meta as { correlationId?: unknown }).correlationId : undefined
  return typeof id === 'string' ? id : ''
}

/** Дополнить прочитанный конверт тем, что знает только мета ответа (для `transformResponse`). */
export function withMeta<T>(read: { data: T; configurationVersion: string }, meta: unknown): NativeRead<T> {
  return { ...read, freshness: freshnessOf(meta), correlationId: correlationOf(meta) }
}

/** Тело запроса контракта: версия, схема действия, ревизия, данные [+ ключи команды]. */
export interface RequestEnvelopeOptions {
  lane: string
  action: string
  configurationVersion: string
  command?: { operationId: string; idempotencyKey: string }
}

export function requestEnvelope<D>(options: RequestEnvelopeOptions, data: D) {
  return {
    contractVersion: '1.0',
    schemaVersion: `onec.business.${options.lane}_${options.action}.v1`,
    expectedConfigurationVersion: options.configurationVersion,
    ...(options.command !== undefined
      ? { operationId: options.command.operationId, idempotencyKey: { value: options.command.idempotencyKey } }
      : {}),
    data,
  }
}
